import React, { useEffect, useRef } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { BrandColor } from '@styleseat/brand';
import { Box, Inline } from '@styleseat/ui';
import {
  BodyText,
  Button,
  Stack,
  TextFormField,
} from '../../../../../ui';
import analytics from '../../../../../../modules/analytics';
import { isValidEmail } from '../../../../../../modules/validators';
import { getIsApp } from '../../../../../../modules/AppInfo';
import {
  useAssignTest,
  useFeatureFlagRedux,
  useInTest,
  useMount,
} from '../../../../../../hooks';
import { GOOGLE_SIGNIN_CLIENT_ID } from '../../../../../../config';
import {
  AUTOPROGRESS_TIMEOUT_MS,
  COM_1138_TEST_NAME,
  COM_1138_TEST_PERCENT,
} from './constants';
import { DISABLED_FACEBOOK_LOGIN_SWITCH } from '../../../../../shared/login/FacebookSignInButton/constants';
import { GoogleSignInButton } from '../../../../../shared/login/GoogleSignInButton/GoogleSignInButton';
import { AppleCheckoutSSOButtons } from '../../../../../shared/login/AppleSignInButton/AppleCheckoutSSOButtons';
import { FacebookCheckoutSignInButton } from '../../../../../shared/login/FacebookSignInButton/FacebookCheckoutSignInButton';
import type { AppleSSOResponse, SetCurrentUserPayload } from '../../../../../../store/CurrentUser.types';
import { EmailChangeEvent } from '../contants';

type InitialEmailStateProps = {
  shouldAutoFocusOnEmail: boolean;
  canDisplaySSO?: boolean;
  emailValue: string;
  passwordValue: string;
  errorMessage: string;
  onChangeEmail: (event: EmailChangeEvent) => void;
  onChangePassword: (newValue: string) => void;
  onClickContinue: (email?: string) => void;
  onKeyPress: (e) => void;
  onEmailFocus: () => void;
  onFacebookSignup: () => Promise<SetCurrentUserPayload>;
  onAppleSignup: (mode: 'signup' | 'login') => Promise<AppleSSOResponse>;
  isLoading: boolean;
  isContinueDisabled: boolean;
  onOpenTOS: () => void;
  onOpenPrivacyPolicy: () => void;
  onAccountCreated: (success: boolean, userData?: any) => void;
};

export const InitialEmailState: React.FCWithChildren<InitialEmailStateProps> = ({
  shouldAutoFocusOnEmail,
  canDisplaySSO = false,
  emailValue = '',
  passwordValue = '',
  errorMessage = '',
  onChangeEmail,
  onChangePassword,
  onClickContinue,
  onKeyPress,
  onEmailFocus,
  onFacebookSignup,
  onAppleSignup,
  isLoading,
  isContinueDisabled,
  onOpenTOS,
  onOpenPrivacyPolicy,
  onAccountCreated,
}) => {
  const isApp = getIsApp();
  const autoProgressTimer = useRef<ReturnType<typeof setTimeout>>();

  // COM-1138: Test showing only Google as an SSO option (no Apple or Facebook) to guest users
  // Test covers users on desktop or mobile browsers only
  const assignCOM1138 = useAssignTest(COM_1138_TEST_NAME, COM_1138_TEST_PERCENT);
  useMount(() => {
    if (!isApp) {
      assignCOM1138();
    }
  });
  const isInCom1138Test = useInTest(COM_1138_TEST_NAME);
  const shouldShowAppleSSO = false;
  const shouldDisplaySSO = canDisplaySSO && isInCom1138Test && !isApp;

  const {
    enabled: isFacebookLoginDisabled,
  } = useFeatureFlagRedux(DISABLED_FACEBOOK_LOGIN_SWITCH);

  useEffect(() => () => {
    if (autoProgressTimer.current) {
      // Clear the timer when unmounting
      clearTimeout(autoProgressTimer.current);
    }
  }, []);

  const autoProgress = (email: string = emailValue) => {
    onChangeEmail({ newValue: email, validate: true });
    if (isValidEmail(email)) {
      analytics.track('progress', {
        component: 'signin_and_signup',
        currentStep: 'initial',
        type: 'automatic',
      });
      onClickContinue(email);
    }
  };

  const handleChangeEmailField = (newValue: string) => {
    if (newValue === null) {
      return;
    }
    onChangeEmail({ newValue, validate: true });

    // Debouncing mechanism: if a timeout is set, clear it and start again.
    // We'll only trigger the autoProgress once the user stops typing.
    if (autoProgressTimer.current) {
      clearTimeout(autoProgressTimer.current);
    }
    autoProgressTimer.current = setTimeout(() => autoProgress(newValue), AUTOPROGRESS_TIMEOUT_MS);
  };

  const handleClickContinue = () => onClickContinue();
  const handleKeyPress = e => {
    if (autoProgressTimer.current) {
      // Clear the timer when progressing with RETURN keypress
      clearTimeout(autoProgressTimer.current);
    }
    onKeyPress(e);
  };

  return (
    <Stack justifyContent="center">
      <TextFormField
        autoFocus={shouldAutoFocusOnEmail}
        accentColor={BrandColor.Red}
        name="email"
        id="email"
        autoComplete="email"
        borderColor={BrandColor.Silver}
        style={{
          borderRadius: 4,
        }}
        message={errorMessage}
        label="Email"
        aria-label="Email"
        type="email"
        value={emailValue}
        onChangeText={handleChangeEmailField}
        onFocus={onEmailFocus}
        onKeyPress={handleKeyPress}
        testID="booking-sign-in-and-up-email-text-field"
      />
      {/* This is required for autocomplete and password manager reasons */}
      {/* @ts-expect-error */}
      <TextFormField
        name="password"
        id="password"
        autoComplete="password"
        style={{
          display: 'none',
        }}
        aria-label="password"
        type="password"
        value={passwordValue}
        onChangeText={onChangePassword}
        testID="booking-sign-in-and-up-password-text-field"
      />
      <Stack
        flexWrap="wrap"
        style={{
          alignContent: 'center',
        }}
      >
        <Box paddingTop={12} width="100%">
          <Button
            loading={isLoading}
            appearance="primary-neue"
            size="large"
            onClick={handleClickContinue}
            testID="sign-in-and-up-continue-button"
            disabled={isContinueDisabled}
          >
            Continue
          </Button>
        </Box>
        {shouldDisplaySSO && (
          <Stack
            space={16}
            paddingTop={16}
            justifyContent="center"
            maxWidth={400}
            width="100%"
          >
            <Inline justifyContent="center">
              <BodyText appearance="small" color={BrandColor.Metal}>or</BodyText>
            </Inline>
            <Box>
              <GoogleOAuthProvider clientId={GOOGLE_SIGNIN_CLIENT_ID}>
                <GoogleSignInButton onAccountCreated={onAccountCreated} />
              </GoogleOAuthProvider>
            </Box>
            {shouldShowAppleSSO && (
              <Box>
                <AppleCheckoutSSOButtons
                  onAppleSignup={onAppleSignup}
                  onAccountCreated={onAccountCreated}
                />
              </Box>
            )}
            {!isFacebookLoginDisabled && (
              <Box>
                <FacebookCheckoutSignInButton
                  onFacebookSignup={onFacebookSignup}
                  onAccountCreated={onAccountCreated}
                />
              </Box>
            )}
            <Inline justifyContent="center" paddingTop={8}>
              <BodyText appearance="smaller" color={BrandColor.Metal} textAlign="center">
                By logging in or signing up I agree to the{'\n'}
                <BodyText appearance="smaller" color={BrandColor.DarkBlue} onPress={onOpenTOS}> terms of
                  service
                </BodyText> and
                <BodyText appearance="smaller" color={BrandColor.DarkBlue} onPress={onOpenPrivacyPolicy}> privacy
                  policy
                </BodyText>
              </BodyText>
            </Inline>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
